import { dbUser } from './init.db.user';
import { logger } from '@shared/lib/logger/logger-manager';
import { IUser, IUserDatabase } from '@shared/interface/model.interface';
import { decryptUser, decryptUsers, encryptUser } from '@shared/lib/crypto.lib';
import { EnvEnum } from '@shared/enum/env.enum';
import { UserStatusEnum } from '@shared/enum/user.enum';

// ********************************************************
// Table 'user' (users who connect locally to the app)
// ********************************************************

/**
 * *********************************************************************
 * PRIVATE FN
 * *********************************************************************
 */

const dbGetCryptedUser = async (
  id?: number,
  decrypt: boolean = true,
): Promise<IUser | undefined> => {
  if (decrypt) {
    const cryptedUser = await dbUser.user.where({ id }).first();
    return cryptedUser
      ? Promise.resolve(decryptUser(cryptedUser))
      : Promise.resolve(undefined);
  } else {
    return dbUser.user.where({ id }).first();
  }
};

const dbGetCryptedUsers = async (
  decrypt: boolean = true,
): Promise<Array<IUser>> => {
  if (decrypt) {
    const users = await dbUser.user.toArray();
    if (users.length === 0) {
      return [];
    } else {
      return decryptUsers(users);
    }
  } else {
    return await dbUser.user.toArray();
  }
};

const dbPutUser = async (user: IUser) => {
  const localUser = await dbGetCryptedUser(user.id);
  if (localUser == null) {
    logger?.error('[dbPutUser] - user is not found');
    return;
  }

  user.uuid = localUser.uuid;
  const encryptedUser = await encryptUser(user);

  if (encryptedUser != null) {
    await dbUser.transaction('rw', dbUser.user, async () => {
      const userToPut: IUserDatabase = {
        id: user.id,
        data: encryptedUser,
        uuid: user.uuid,
      };
      if (process.env.ENV === EnvEnum.LOCAL) {
        userToPut.uncryptedData = user;
      }
      await dbUser.user.put(userToPut);
    });
  } else {
    logger?.error('[dbPutUser] - encryptedUser is null');
  }
};

const dbAddUser = async (user: IUser) => {
  const encryptedUser = await encryptUser(user);

  if (encryptedUser != null) {
    await dbUser.transaction('rw', dbUser.user, async () => {
      const userToAdd: IUserDatabase = {
        id: user.id,
        data: encryptedUser,
        uuid: user.uuid,
      };
      if (process.env.ENV === EnvEnum.LOCAL) {
        userToAdd.uncryptedData = user;
      }
      await dbUser.user.add(userToAdd);
    });
  } else {
    logger?.error('[dbAddUser] - encryptedUser is null');
  }
};

/**
 * *********************************************************************
 * PUBLIC FN
 * *********************************************************************
 */

// ****************************************************************************** GET
interface IDbGetUserParam {
  id?: number;
  email?: string;
}
export const dbGetUser = async (
  params: IDbGetUserParam,
): Promise<IUser | undefined> => {
  const users = await dbGetCryptedUsers();

  return users.find((u) => {
    return params.id === u.id || params.email === u.email;
  });
};

export const dbGetUsers = async (
  status?: UserStatusEnum,
): Promise<Array<IUser>> => {
  const users = await dbGetCryptedUsers();
  if (status == null) {
    return users;
  } else {
    return users.filter((u) => u.status === status);
  }
};

export const dbGetActiveUser = async (): Promise<IUser | undefined> => {
  const users = await dbGetCryptedUsers();

  return users.find((u) => u.isActive === true);
};

export const dbGetLocalUsers = async (): Promise<Array<IUser>> => {
  return await dbGetCryptedUsers();
};

// ****************************************************************************** SET
/**
 *
 * @param param0 IDbSetActiveUserParam
 * @returns
 */
interface IDbSetActiveUserParam {
  id?: number;
  email?: string;
  isConnected?: boolean;
}
export const dbSetActiveUser = async (params: IDbSetActiveUserParam) => {
  const users = await dbGetCryptedUsers();
  const activeUser = users.find((u) => u.isActive === true);

  // Found wanted user
  const foundUser = users.find(
    (u) => u.id === params.id || u.email === params.email,
  );

  // Deactivate active User
  if (activeUser != null && activeUser.id !== params.id) {
    activeUser.isActive = false;
    await dbPutUser(activeUser);
  }

  if (foundUser) {
    foundUser.isActive = true;
    if (params.isConnected != null) foundUser.isConnected = params.isConnected;

    // Update the user
    await dbPutUser(foundUser);
  }
};

export const dbUpdateActiveUsername = async (newUsername: string) => {
  const user = await dbGetActiveUser();
  if (user) {
    user.username = newUsername;

    await dbPutUser(user);
  }
};

export const dbDisconnectActiveUser = async () => {
  const user = await dbGetActiveUser();
  if (user) {
    user.isConnected = false;

    await dbPutUser(user);
  }
};

export const dbLoggoutActiveUser = async () => {
  const user = await dbGetActiveUser();
  if (user) {
    user.isConnected = false;
    user.jwt_hp = undefined;
    user.refreshToken = undefined;

    await dbPutUser(user);
  }
};

export const dbDisconnectAllUsers = async () => {
  const users = await dbGetLocalUsers();
  for (const user of users) {
    user.isConnected = false;
    await dbPutUser(user);
  }
};

export const dbDisableAllUsers = async () => {
  const users = await dbGetLocalUsers();
  for (const user of users) {
    user.isActive = false;
    await dbPutUser(user);
  }
};

export const dbUpdateUserRoles = async (providedUser: IUser) => {
  // Update user's roles
  const user = await dbGetCryptedUser(providedUser.id);

  if (user) {
    user.roles = providedUser.roles;

    await dbPutUser(user);
  }
};

export const dbResetRefreshToken = async function () {
  const activeUser = await dbGetActiveUser();

  if (activeUser) {
    activeUser.refreshToken = undefined;
    await dbPutUser(activeUser);
  }
};

export const dbSetUser = async (params: IUser) => {
  const activeUser = await dbGetActiveUser();

  // Get the current user
  let users = await dbGetCryptedUsers();

  // First, inactive all users
  if (users.length > 0) {
    users = [
      ...users.map((u) => {
        return {
          ...u,
          isActiveLocally: 0,
        };
      }),
    ];
  }

  const currentUser = users.find(
    (u) => u.id === params.id || u.email === params.email,
  );

  const userIsActiveUser = (user: IUser) =>
    user?.isActive || activeUser?.id != null && user.id === activeUser?.id;

  if (currentUser == null) {
    // Add the new user
    const isActive = userIsActiveUser(params);

    await dbAddUser({
      ...params,
      isActive,
      isConnected: params.isConnected === true,
      isActiveLocally: 0,
      chkRemember: params.chkRemember === true
    });
  } else {
    currentUser.isActive = userIsActiveUser(currentUser);
    if (params.jwt_hp != null) currentUser.jwt_hp = params.jwt_hp;
    if (params.chkRemember != null)
      currentUser.chkRemember = params.chkRemember;
    if (params.refreshToken != null)
      currentUser.refreshToken = params.refreshToken;
    if (params.isConnected != null)
      currentUser.isConnected = params.isConnected;
    if (params.lastConnectionAt != null)
      currentUser.lastConnectionAt = params.lastConnectionAt;
    if (params.newConnectionAt != null)
      currentUser.newConnectionAt = params.newConnectionAt;
    if (params.signupAt != null) currentUser.signupAt = params.signupAt;
    if (params.status != null) currentUser.status = params.status;

    await dbPutUser(currentUser);
  }
};

export const dbSetUsers = async (users?: IUser[]) => {
  if (users != null) {
    // Insert each user
    for (const providedUser of users) {
      // providedUser.uuid = crypto.randomUUID();
      await dbSetUser(providedUser);
    }
  }
};

// ****************************************************************************** DELETE
export const dbRemoveLocalUser = async ({ email }: { email?: string }) => {
  const users = await dbGetCryptedUsers();

  return dbUser.transaction('rw', dbUser.user, async () => {
    // Get crypted users
    const user = users.find((u) => u.email === email);

    if (user) {
      await dbUser.user
        .where({ id: user.id })
        .delete()
        .catch((err) => logger?.error(err));
    }
  });
};
